/**
 * Tracks whether the player has ever cleared a song in any mode.
 *
 * Drives how much in-game hand-holding to show: a brand-new player who
 * has never finished a run gets teaching hints (e.g. "CLICK ON THE
 * CIRCLE!", "HOLD TO THE END!") far more often than someone who has
 * already cleared at least one song. Stored client-side in localStorage
 * so the signal survives across visits for anonymous players without a
 * server roundtrip.
 */

const SONG_COMPLETED_KEY = 'gd-song-completed:v1';

/** True once the player has cleared at least one real (non-replay) song. */
export function hasCompletedAnySong(): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }
  return localStorage.getItem(SONG_COMPLETED_KEY) === '1';
}

/** Record that the player has cleared at least one song. Idempotent. */
export function markSongCompleted(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (localStorage.getItem(SONG_COMPLETED_KEY) === '1') {
    return;
  }
  try {
    localStorage.setItem(SONG_COMPLETED_KEY, '1');
  } catch (err) {
    console.error('Failed to persist song-completed flag:', err);
  }
}

const TUTORIAL_HINTS_BEFORE_FIRST_SONG = 10;
const TUTORIAL_HINTS_AFTER_FIRST_SONG = 3;

/**
 * Per-run budget for repeating in-game teaching hints (e.g. "CLICK ON
 * THE CIRCLE!", "HOLD TO THE END!", "WAIT FOR THE PAD!"). Brand-new
 * players who've never cleared a song get a generous budget; once
 * they've finished one, hints become rare so they don't nag. Each hint
 * type tracks its own counter against this shared per-run cap.
 */
export function tutorialHintBudget(): number {
  return hasCompletedAnySong()
    ? TUTORIAL_HINTS_AFTER_FIRST_SONG
    : TUTORIAL_HINTS_BEFORE_FIRST_SONG;
}
